import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { SI_PORTAL_BASE_PATH } from '../../siPortal.definitions';
import { categoriaDocumento, cienciaCobradaDe, cienciaEmDia, millis, situacaoPrazo, textoPrazo } from '../publicacao.definitions';
import { useMeusEstadosQuery, usePublicacaoQuery } from '../usePublicacoes';

interface DocumentoRelacionadoNoPostProps {
  documentoId: string;
  colaboradorId: string | null | undefined;
}

/** No post aberto: atalho pro documento oficial e, se ele pede ciência, "Ver documento e confirmar"
 * (a ciência é só do documento — confirmar lá vale pros dois). */
export function DocumentoRelacionadoNoPost({ documentoId, colaboradorId }: DocumentoRelacionadoNoPostProps) {
  const agora = useMemo(() => Date.now(), []);
  const { data: documento } = usePublicacaoQuery(documentoId);
  const lista = useMemo(() => (documento ? [documento] : []), [documento]);
  const { estados } = useMeusEstadosQuery(lista);
  if (!documento || documento.tipo !== 'documento' || documento.status !== 'publicada') return null;

  const link = `${SI_PORTAL_BASE_PATH}/documentos/${documento.id}`;
  const categoria = categoriaDocumento(documento.categoria);
  const cobrada = cienciaCobradaDe(documento, colaboradorId);
  const ciente = cienciaEmDia(documento, estados.get(documento.id));
  const situacao = situacaoPrazo(documento, agora);
  const prazo = millis(documento.ciencia?.prazo);

  return (
    <div className="space-y-3">
      <Link
        to={link}
        className="flex items-center gap-3 rounded-xl border border-primary-200 bg-primary-50/50 px-4 py-3 text-sm transition-colors hover:border-primary-300"
      >
        <DocumentTextIcon className="h-6 w-6 shrink-0 text-primary-600" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-semibold text-primary-700">Documento relacionado · {categoria.label}</span>
          <span className="block truncate font-semibold text-raro-gray-900">
            {documento.titulo} <span className="font-normal text-raro-gray-500">· v{documento.versao ?? 1}</span>
          </span>
        </span>
        <span className="shrink-0 text-xs font-semibold text-primary-700">Ver documento →</span>
      </Link>

      {cobrada && (
        <div
          className={`flex flex-col gap-3 rounded-2xl border px-5 py-4 sm:flex-row sm:items-center sm:justify-between ${
            ciente ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'
          }`}
        >
          <div>
            <p className={`text-sm font-semibold ${ciente ? 'text-green-900' : 'text-amber-900'}`}>
              {ciente ? 'Você confirmou a ciência deste documento' : 'Este documento pede sua ciência'}
            </p>
            {!ciente && (
              <p className={`text-xs ${situacao.tipo === 'atrasada' ? 'font-semibold text-rose-700' : 'text-amber-800'}`}>
                {prazo ? textoPrazo(situacao) : 'Sem prazo'} · a confirmação é feita no documento
              </p>
            )}
          </div>
          {ciente ? (
            <CheckCircleIcon className="h-7 w-7 text-green-600" aria-hidden="true" />
          ) : (
            <Link to={link} className="rounded-full bg-primary-600 px-5 py-2.5 text-center text-sm font-semibold text-white hover:bg-primary-700">
              Ver documento e confirmar
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
